/**
 * The contribution grid — one square per day, shaded by how much happened.
 *
 * WHY THIS EXISTS
 *   Lattice's whole premise is that the change log is the source of truth. The
 *   grid is that log seen from far away: five months of an instrument team's
 *   work in one glance, so "when did the LCSEM1 drawings actually get done?"
 *   is answered without opening anything.
 *
 * DATA DEFINITIONS
 *
 *   A Level is one of 0, 1, 2, 3, 4.
 *   Interpretation: how much INK a square is drawn with. 0 is "nothing
 *   happened"; 4 is "as busy as this workspace gets". A Level is a rank, not a
 *   count — two level-3 days are comparably busy, not equally busy.
 *
 *   A Square is { day, index, level, future, label }.
 *   Interpretation: one cell, ready to draw. `index` is its position in the
 *   grid's flat `days` array, which is the only identity the keyboard cursor,
 *   the hover state and the click handler ever need to pass around. `future`
 *   days are the trailing padding buildGrid adds to finish the last week;
 *   they are drawn as nothing, because a blank square would claim, falsely,
 *   that nobody worked that day.
 *
 *   A Geometry is the constant block below.
 *   Interpretation: every position in this component is arithmetic on these
 *   constants, never a measurement. That is deliberate — the grid can render,
 *   move its cursor and place its tooltip without ever reading layout back
 *   out of the DOM, so there is no reflow and nothing can shift.
 *
 * THIS COMPONENT IS PURE PRESENTATION: props in, pixels out. No store, no
 * fetching. The only ambient fact it reads is the clock, once, to know which
 * day "the last year" ends on.
 */
import * as React from 'react'
import {
  buildGrid, dayKeyOf, addDays, daysBetween, intensityOf,
  type Change, type ContributionDay, type DayKey, type User, type UserId,
} from '@lattice/core'
import { cn } from '@/lib/utils'
import { EmptyState } from '@/components/ui/primitives'
import { CalendarDays } from 'lucide-react'

/* ------------------------------ Geometry ------------------------------ */

const CELL = 11
const GAP = 3
const STEP = CELL + GAP
/** Width of the Mon/Wed/Fri gutter down the left. */
const GUTTER = 26
/** Height of the month-label strip. */
const MONTH_H = 15
/**
 * Height reserved above the grid for the hover tooltip. Reserved ALWAYS, even
 * when nothing is hovered: a tooltip that pushes the page around is worse than
 * a little whitespace. Measured in a browser, not guessed: two tight lines plus
 * padding and border come to 37px, so 38 clears the month strip.
 */
const TIP_H = 38
/**
 * Half the WIDEST tooltip, measured: a day with all four kinds renders 314px.
 * Used to decide when to stop centring, never to clip.
 */
const TIP_HALF = 160
const ROWS = 7

/* ------------------------------ Formatting ---------------------------- */

/**
 * Dates are formatted in a FIXED locale and in UTC.
 *
 * UTC because a DayKey is already the team's own calendar day; re-interpreting
 * it in the reader's timezone would slide it by one. Fixed locale because a
 * shared workspace where two people describe the same square differently is a
 * bug, not a feature.
 */
const DAY_FMT = new Intl.DateTimeFormat('en-GB', {
  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
})
const MONTH_FMT = new Intl.DateTimeFormat('en-GB', { month: 'short', timeZone: 'UTC' })
const RANGE_FMT = new Intl.DateTimeFormat('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' })

/** The instant a DayKey names, as a Date. asDate : DayKey -> Date */
const asDate = (day: DayKey): Date => new Date(`${day}T00:00:00Z`)

/** "Wednesday 12 August 2026". formatDay : DayKey -> String */
const formatDay = (day: DayKey): string => DAY_FMT.format(asDate(day))

/** "No changes" / "1 change" / "3 changes". countPhrase : Number -> String */
const countPhrase = (n: number): string =>
  n === 0 ? 'No changes' : `${n} change${n === 1 ? '' : 's'}`

/**
 * The kinds that actually occurred, in the order provenance is usually read.
 * A day with only additions should not be padded with "0 deleted".
 * breakdownOf : ContributionDay -> String
 */
function breakdownOf(day: ContributionDay): string {
  const parts: string[] = []
  if (day.added) parts.push(`${day.added} added`)
  if (day.modified) parts.push(`${day.modified} modified`)
  if (day.deleted) parts.push(`${day.deleted} deleted`)
  if (day.renamed) parts.push(`${day.renamed} renamed`)
  return parts.join(', ')
}

/* -------------------------------- Shading ----------------------------- */

/**
 * How much foreground ink each level mixes into the empty-square colour.
 *
 * WHY MONOCHROME, AND WHY THIS RAMP
 *   GitHub's green would be the only saturated thing on a screen whose entire
 *   point is to stay out of the way of the drawings. So the ramp is built from
 *   --fg over --bg-inset: literally "how much was written that day". Because
 *   both ends are theme tokens, the ramp inverts itself — dark ink on a pale
 *   square in light mode, bright ink on a near-black square in dark mode — and
 *   --accent stays reserved for the one thing accent should mean: selection.
 */
const INK_MIX: readonly number[] = [0, 16, 34, 58, 88]

/** The fill for a square at a given level. shadeOf : Level -> String */
const shadeOf = (level: number): string =>
  level <= 0
    ? 'var(--bg-inset)'
    : `color-mix(in oklch, var(--fg) ${INK_MIX[level]}%, var(--bg-inset))`

/** A hairline so an empty square still reads as a square, not as background. */
const HAIRLINE = 'inset 0 0 0 1px color-mix(in oklch, var(--fg) 7%, transparent)'

/**
 * Compress a day's count before ranking it.
 *
 * WHY: intensityOf cuts the range into four EQUAL slices of `total / maxTotal`.
 * That rule is right; applied to a raw count it is applied to the wrong scale.
 * This team's activity is bursty — a bulk import of 40 files sits beside dozens
 * of two-file days — so against a max of 40, everything from 1 to 10 lands in
 * the palest bucket and the grid reads as blank with a few specks. Measured on
 * a log shaped like the real one (776 changes, 162 days, max 40): the linear
 * ramp put 77 of 85 active days in level 1; the square root spreads them
 * 25 / 52 / 1 / 7.
 *
 * Square root rather than log because it is the same compression cartography
 * uses for proportional symbols — perceived weight tracks the square root of
 * the quantity — and because it keeps a one-change day at the faintest level
 * instead of promoting it. The thresholds themselves stay in @lattice/core;
 * we only change the scale they are measured on.
 *
 * levelOf : ContributionDay Number -> Level
 */
const levelOf = (day: ContributionDay, maxTotal: number): 0 | 1 | 2 | 3 | 4 =>
  intensityOf({ ...day, total: Math.sqrt(day.total) }, Math.sqrt(maxTotal))

/* -------------------------------- Window ------------------------------ */

/**
 * A DayRange is { from, to }.
 * Interpretation: the inclusive span of calendar days the grid draws.
 */
interface DayRange { readonly from: DayKey; readonly to: DayKey }

/**
 * Choose the window to draw.
 *
 * "The last year" is the familiar frame and the default. But a workspace whose
 * history is entirely older than that would render as 365 blank squares, which
 * is technically true and completely useless — so we fall back to the span of
 * the data itself and say so in the summary line.
 *
 * DayKeys are "YYYY-MM-DD", so lexicographic order IS chronological order.
 * rangeFor : [Change] DayKey -> DayRange
 */
function rangeFor(changes: readonly Change[], today: DayKey): DayRange {
  const yearFrom = addDays(today, -364)
  if (changes.length === 0) return { from: yearFrom, to: today }
  const days = changes.map(c => dayKeyOf(c.at))
  if (days.some(d => d >= yearFrom)) return { from: yearFrom, to: today }
  const sorted = [...days].sort()
  return { from: sorted[0], to: sorted[sorted.length - 1] }
}

/* --------------------------------- Cells ------------------------------ */

interface Square {
  readonly day: ContributionDay
  readonly index: number
  readonly level: 0 | 1 | 2 | 3 | 4
  readonly future: boolean
  readonly label: string
}

/**
 * The squares, transposed into seven weekday rows.
 *
 * buildGrid hands back week COLUMNS, but the DOM is built row-major on purpose:
 * a screen reader then walks "Monday: 3 changes, 0, 12…" along a weekday, which
 * is a sentence, and role="row" means what ARIA says it means.
 * toRows : ContributionGrid-like -> [[Square]]
 */
function toRows(
  weeks: readonly (readonly ContributionDay[])[],
  maxTotal: number,
  todayIndex: number,
): Square[][] {
  const rows: Square[][] = Array.from({ length: ROWS }, () => [])
  weeks.forEach((week, w) => {
    week.forEach((day, r) => {
      const index = w * ROWS + r
      const future = index > todayIndex
      rows[r].push({
        day,
        index,
        level: levelOf(day, maxTotal),
        future,
        label: future
          ? formatDay(day.date)
          : `${countPhrase(day.total)} on ${formatDay(day.date)}` +
            (day.total === 0 ? '' : `: ${breakdownOf(day)}`),
      })
    })
  })
  return rows
}

/**
 * Draw the squares.
 *
 * Memoised and deliberately ignorant of hover, cursor and selection: those are
 * three absolutely-positioned rings drawn OVER this subtree. Moving a ring
 * therefore re-renders three divs, not three hundred and seventy-one, which is
 * the difference between a grid that tracks the pointer and one that lags it.
 */
const Cells = React.memo(function Cells({ rows, idBase, selectedIndex }: {
  rows: readonly Square[][]; idBase: string; selectedIndex: number | null
}) {
  return (
    <>
      {rows.map((row, r) => (
        <div
          key={r}
          role="row"
          className="absolute flex"
          style={{ top: r * STEP, left: 0, gap: GAP }}
        >
          {row.map(sq => (
            <div
              key={sq.index}
              id={`${idBase}-${sq.index}`}
              role="gridcell"
              // A future day is still a cell — role="row" owns gridcells and
              // nothing else, and a hole in the row makes a screen reader
              // miscount the week. It is announced as disabled instead, which
              // is exactly what it is: a day that has not happened yet.
              aria-disabled={sq.future || undefined}
              data-i={sq.future ? undefined : sq.index}
              aria-label={sq.label}
              // Only the selected cell carries the state. Marking all 371
              // would make a reader say "not selected" 371 times.
              aria-selected={sq.index === selectedIndex || undefined}
              className={sq.future ? undefined : 'cursor-pointer'}
              style={{
                width: CELL,
                height: CELL,
                borderRadius: 2,
                background: sq.future ? 'transparent' : shadeOf(sq.level),
                boxShadow: sq.future ? undefined : HAIRLINE,
              }}
            />
          ))}
        </div>
      ))}
    </>
  )
})

/* --------------------------------- Rings ------------------------------ */

/**
 * A ring drawn over one square. Position is arithmetic, never measured.
 *
 * `spread` lets two rings sit on the SAME square concentrically, which is the
 * case that matters: after you press Enter, the day is both selected and under
 * the keyboard cursor, and a keyboard-first product must never lose the cursor.
 */
function Ring({ index, color, spread = 2, dim }: {
  index: number; color: string; spread?: number; dim?: boolean
}) {
  const w = Math.floor(index / ROWS)
  const r = index % ROWS
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute"
      style={{
        left: w * STEP - spread,
        top: r * STEP - spread,
        width: CELL + spread * 2,
        height: CELL + spread * 2,
        borderRadius: 4,
        border: `2px solid ${color}`,
        opacity: dim ? 0.55 : 1,
      }}
    />
  )
}

/* ------------------------------ Sub-views ----------------------------- */

/** Only three weekdays are labelled; seven would be noise beside 11px squares. */
const WEEKDAY_LABELS: readonly string[] = ['', 'Mon', '', 'Wed', '', 'Fri', '']

/** The month strip along the top. MonthLabels : {weeks} -> Element */
function MonthLabels({ weeks }: { weeks: readonly (readonly ContributionDay[])[] }) {
  return (
    <>
      {monthLabelsFor(weeks).map(({ w, text }) => (
        <div
          key={w}
          className="absolute text-[10px] text-[var(--fg-subtle)]"
          style={{ left: GUTTER + w * STEP, top: TIP_H, lineHeight: `${MONTH_H}px` }}
        >
          {text}
        </div>
      ))}
    </>
  )
}

/** The Mon/Wed/Fri gutter down the left. WeekdayGutter : -> Element */
function WeekdayGutter() {
  return (
    <>
      {WEEKDAY_LABELS.map((text, r) => text && (
        <div
          key={r}
          className="absolute text-[10px] text-[var(--fg-subtle)]"
          style={{ left: 0, top: TIP_H + MONTH_H + r * STEP, height: CELL, lineHeight: `${CELL}px` }}
        >
          {text}
        </div>
      ))}
    </>
  )
}

/**
 * The hover/cursor readout, in the band reserved for it at the top.
 *
 * It fades rather than pops: `pop-in` animates `transform`, and an animated
 * transform beats the inline one that centres this tooltip, so the thing would
 * slide in from half its own width away.
 *
 * It is hidden from assistive technology on purpose. As a live region it fired
 * once per square the pointer crossed — a hundred polite announcements for one
 * flick of the wrist — and it says nothing the focused cell's own aria-label
 * does not already say through aria-activedescendant.
 * Tip : {day, index, innerWidth} -> Element
 */
function Tip({ day, index, innerWidth }: {
  day: ContributionDay; index: number; innerWidth: number
}) {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute fade-in whitespace-nowrap rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--bg-raised)] px-2 py-[3px] shadow-pop"
      style={{ top: 0, ...tipAnchorOf(GUTTER + Math.floor(index / ROWS) * STEP + CELL / 2, innerWidth) }}
    >
      <div className="text-xs leading-4 text-[var(--fg)]">
        <span className="tabular font-medium">{countPhrase(day.total)}</span>
        {day.total > 0 && <span className="text-[var(--fg-muted)]"> · {breakdownOf(day)}</span>}
      </div>
      <div className="text-[10px] leading-[13px] text-[var(--fg-subtle)]">{formatDay(day.date)}</div>
    </div>
  )
}

/** The sentence and the ramp key beneath the grid. GridFooter : ... -> Element */
function GridFooter({ total, authorName, range, isYearRange }: {
  total: number; authorName?: string; range: DayRange; isYearRange: boolean
}) {
  return (
    <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
      <div className="text-xs text-[var(--fg-muted)]">
        <span className="tabular text-[var(--fg)]">{total.toLocaleString('en-GB')}</span>
        {' '}change{total === 1 ? '' : 's'}
        {authorName ? ` by ${authorName}` : ''}
        {isYearRange
          ? ' in the last year'
          : ` between ${RANGE_FMT.format(asDate(range.from))} and ${RANGE_FMT.format(asDate(range.to))}`}
        {total === 0 && <span className="text-[var(--fg-subtle)]"> — nothing here yet</span>}
      </div>

      <div className="flex items-center gap-1.5 text-[10px] text-[var(--fg-subtle)]">
        <span>Less</span>
        {[0, 1, 2, 3, 4].map(l => (
          <span key={l} aria-hidden
            style={{ width: CELL, height: CELL, borderRadius: 2, background: shadeOf(l), boxShadow: HAIRLINE }} />
        ))}
        <span>More</span>
      </div>
    </div>
  )
}

/* --------------------------------- Model ------------------------------ */

/**
 * A GridModel is everything the view needs that is a pure function of the log.
 * Interpretation: one derivation, memoised on the change list, so that hovering
 * a square recomputes nothing at all.
 */
interface GridModel {
  readonly grid: ReturnType<typeof buildGrid>
  readonly range: DayRange
  readonly rows: readonly Square[][]
  /** The changes that landed on each day, ready for the click callback. */
  readonly byDay: ReadonlyMap<DayKey, Change[]>
  /** The last index a person may navigate to: nothing after today exists yet. */
  readonly todayIndex: number
  /** Where the keyboard starts: the most recent day that actually has work. */
  readonly initialCursor: number
}

/** Derive the whole model from the log. useGridModel : ... -> GridModel */
function useGridModel(
  changes: readonly Change[], authorId: UserId | undefined, today: DayKey,
): GridModel {
  const mine = React.useMemo(
    () => (authorId ? changes.filter(c => c.authorId === authorId) : changes),
    [changes, authorId],
  )

  return React.useMemo(() => {
    const range = rangeFor(mine, today)
    const grid = buildGrid(mine, range.from, range.to)

    // Indexed once rather than filtered inside the click handler: fine either
    // way at 776 changes, embarrassing at 100,000.
    const byDay = new Map<DayKey, Change[]>()
    for (const c of mine) {
      const key = dayKeyOf(c.at)
      const list = byDay.get(key)
      if (list) list.push(c)
      else byDay.set(key, [c])
    }

    const todayIndex = Math.min(daysBetween(grid.from, today), grid.days.length - 1)

    let initialCursor = Math.max(0, todayIndex)
    for (let i = todayIndex; i >= 0; i--) {
      if (grid.days[i].total > 0) { initialCursor = i; break }
    }

    return {
      grid, range, byDay, todayIndex, initialCursor,
      rows: toRows(grid.weeks, grid.maxTotal, todayIndex),
    }
  }, [mine, today])
}

/* -------------------------------- Component --------------------------- */

export interface ContributionGridProps {
  readonly changes: readonly Change[]
  readonly roster: readonly User[]
  /**
   * The day "the last year" ends on. Passing it keeps the component a pure
   * function of its props; omitting it falls back to this machine's clock,
   * read once.
   */
  readonly today?: DayKey
  /** Called with the day and exactly the changes that landed on it. */
  onSelectDay?: (date: DayKey, changes: Change[]) => void
  /**
   * The highlighted day, when the caller owns selection.
   *
   * Selection is controlled if this key is PRESENT AT ALL, even as undefined —
   * that is how a parent says "I own this", and it is what lets a parent clear
   * the highlight by toggling the same day off.
   */
  readonly selectedDay?: string
  /** Show only one person's work. Undefined means everybody. */
  readonly authorId?: UserId
  readonly className?: string
}

/**
 * A year of a workspace's activity as a keyboard-navigable heat grid.
 *
 * Weeks run left to right as columns, weekdays top to bottom with Sunday first
 * — the shape everyone already knows how to read, so nobody has to learn it.
 *
 * ContributionGrid : ContributionGridProps -> Element
 */
export function ContributionGrid(props: ContributionGridProps) {
  const { changes, roster, onSelectDay, authorId, className, today: givenToday } = props
  const controlled = 'selectedDay' in props

  const idBase = React.useId()
  const scroller = React.useRef<HTMLDivElement>(null)

  // Without a `today` prop the clock is read once per mount. Re-reading it on
  // every render would let the grid silently redefine "the last year" mid-session.
  const today = React.useMemo(
    () => givenToday ?? dayKeyOf(new Date().toISOString()),
    [givenToday],
  )

  const { grid, range, rows, byDay, todayIndex, initialCursor } =
    useGridModel(changes, authorId, today)

  const [cursor, setCursor] = React.useState(initialCursor)
  const [hover, setHover] = React.useState<number | null>(null)
  const [focused, setFocused] = React.useState(false)
  // Held as a DAY, not an index: indices are relative to a window that changes
  // when you switch project or person, and a stale index would ring the wrong
  // square. A day means the same thing in every window.
  const [ownSelection, setOwnSelection] = React.useState<string | null>(null)

  const selectedKey = controlled ? props.selectedDay ?? null : ownSelection

  /** Where the selected day sits in this window, if it is in it at all. */
  const selected = React.useMemo(() => {
    if (selectedKey === null) return null
    const i = daysBetween(grid.from, selectedKey as DayKey)
    return i >= 0 && i < grid.days.length ? i : null
  }, [selectedKey, grid])

  /**
   * A WindowKey identifies the span of days an index refers to.
   * Interpretation: two renders with the same key agree on what "index 200"
   * means, so the cursor and the scroll position are still valid. Appending a
   * change does NOT change the key — and that is the point. The log grows
   * constantly in a live workspace, and neither the keyboard cursor nor the
   * horizontal scroll may be yanked away while someone is reading.
   */
  const windowKey = `${grid.from}:${grid.days.length}`

  // Reset during render rather than in an effect: an effect would paint one
  // frame with a cursor pointing into the previous window.
  const [lastWindow, setLastWindow] = React.useState(windowKey)
  if (lastWindow !== windowKey) {
    setLastWindow(windowKey)
    setCursor(initialCursor)
    setHover(null)
  }
  // ownSelection is intentionally NOT cleared: it is held as a day, which means
  // the same thing in every window, and `selected` already resolves to null
  // when that day is out of view. Clearing it would silently disagree with a
  // parent that is still filtering its feed by that day.

  // Open on the present, not on last October. A layout effect, so the jump
  // happens before paint and is never seen.
  React.useLayoutEffect(() => {
    const el = scroller.current
    if (el) el.scrollLeft = el.scrollWidth
  }, [windowKey])

  const select = React.useCallback((index: number) => {
    const day = grid.days[index]
    if (!day) return
    // Toggle when we own the selection; a controlled parent decides for itself.
    setOwnSelection(current => (current === day.date ? null : day.date))
    onSelectDay?.(day.date, byDay.get(day.date) ?? [])
  }, [grid, byDay, onSelectDay])

  /** Keep the cursor's column on screen after an arrow key. */
  const revealColumn = React.useCallback((index: number) => {
    const el = scroller.current
    if (!el) return
    const x = GUTTER + Math.floor(index / ROWS) * STEP
    if (x < el.scrollLeft) el.scrollLeft = Math.max(0, x - GUTTER)
    else if (x + CELL > el.scrollLeft + el.clientWidth) el.scrollLeft = x + CELL - el.clientWidth + GUTTER
  }, [])

  const moveTo = React.useCallback((index: number) => {
    const clamped = Math.max(0, Math.min(index, todayIndex))
    setCursor(clamped)
    revealColumn(clamped)
  }, [todayIndex, revealColumn])

  const onKeyDown = React.useCallback((e: React.KeyboardEvent) => {
    const row = cursor % ROWS
    const step = (columns: number) => moveTo(columnStep(cursor, columns, todayIndex))
    // Ctrl/Cmd widens Home and End from "this weekday" to "the whole grid",
    // which is what the ARIA grid pattern says those keys mean.
    const whole = e.ctrlKey || e.metaKey
    switch (e.key) {
      case 'ArrowUp': if (row > 0) moveTo(cursor - 1); break
      case 'ArrowDown': if (row < ROWS - 1 && cursor + 1 <= todayIndex) moveTo(cursor + 1); break
      case 'ArrowLeft': step(-1); break
      case 'ArrowRight': step(1); break
      case 'PageUp': step(-4); break
      case 'PageDown': step(4); break
      case 'Home': moveTo(whole ? 0 : row); break
      case 'End': moveTo(whole ? todayIndex : columnStep(cursor, Infinity, todayIndex)); break
      case 'Enter': case ' ': select(cursor); break
      default: return
    }
    e.preventDefault()
  }, [cursor, moveTo, todayIndex, select])

  if (changes.length === 0) {
    return (
      <div className={className}>
        <EmptyState
          icon={CalendarDays}
          title="No activity yet"
          hint="Every upload, rename and deletion lands here. The grid fills itself in."
        />
      </div>
    )
  }

  const weeks = grid.weeks.length
  const innerWidth = GUTTER + weeks * STEP - GAP
  // A pointer that was over a square when the window shrank never gets a
  // pointerleave, so the stale index has to be rejected here or a ring is
  // drawn over a column that no longer exists.
  const hovered = hover !== null && hover <= todayIndex ? hover : null
  const tipIndex = hovered ?? (focused ? cursor : null)
  const tipDay = tipIndex === null ? undefined : grid.days[tipIndex]
  const author = authorId ? roster.find(u => u.id === authorId) : undefined

  return (
    <div className={cn('select-none', className)}>
      <div ref={scroller} className="overflow-x-auto overflow-y-hidden">
        <div
          className="relative"
          style={{ width: innerWidth, height: TIP_H + MONTH_H + ROWS * STEP - GAP }}
        >
          <MonthLabels weeks={grid.weeks} />
          <WeekdayGutter />

          <div
            role="grid"
            aria-label={author ? `Contribution grid for ${author.name}` : 'Contribution grid'}
            aria-activedescendant={`${idBase}-${cursor}`}
            tabIndex={0}
            onKeyDown={onKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onPointerOver={e => { const i = indexFromEvent(e); if (i !== null) setHover(i) }}
            onPointerLeave={() => setHover(null)}
            onClick={e => {
              const i = indexFromEvent(e)
              if (i !== null) { setCursor(i); select(i) }
            }}
            className="absolute rounded-[var(--radius-sm)]"
            style={{
              left: GUTTER,
              top: TIP_H + MONTH_H,
              width: weeks * STEP - GAP,
              height: ROWS * STEP - GAP,
            }}
          >
            <Cells rows={rows} idBase={idBase} selectedIndex={selected} />

            {/* Three rings, drawn over the memoised squares. */}
            {hovered !== null && hovered !== cursor && hovered !== selected && (
              <Ring index={hovered} color="var(--fg-muted)" dim />
            )}
            {selected !== null && <Ring index={selected} color="var(--accent)" />}
            {focused && (
              <Ring index={cursor} color="var(--fg)" spread={cursor === selected ? 4 : 2} />
            )}
          </div>

          {tipDay && tipIndex !== null && (
            <Tip day={tipDay} index={tipIndex} innerWidth={innerWidth} />
          )}
        </div>
      </div>

      <GridFooter
        total={grid.totalChanges}
        authorName={author?.name}
        range={range}
        isYearRange={range.to === today}
      />
    </div>
  )
}

/**
 * The square an event happened on, or null if it missed one.
 *
 * One listener serves every square. Delegation is not a micro-optimisation
 * here: it is what lets the cell subtree stay memoised and never re-render
 * while the pointer moves across it.
 * indexFromEvent : SyntheticEvent -> Number | null
 */
function indexFromEvent(e: React.SyntheticEvent): number | null {
  const raw = (e.target as HTMLElement).closest('[data-i]')?.getAttribute('data-i')
  return raw == null ? null : Number(raw)
}

/**
 * The last column a weekday row can reach, given the last selectable index.
 *
 * The final column is RAGGED — it stops at today — so on a Sunday only row 0
 * exists in it, and on a Friday rows 0..5 do.
 * lastColumnOf : Number Number -> Number
 */
export function lastColumnOf(row: number, todayIndex: number): number {
  const last = Math.floor(todayIndex / ROWS)
  return row <= todayIndex % ROWS ? last : last - 1
}

/**
 * Move the cursor `columns` weeks sideways, KEEPING THE WEEKDAY.
 *
 * Clamping a sideways move by the flat index instead — Math.min(i + 7, today)
 * — slides the cursor down the ragged edge onto a different weekday: press
 * Right from Saturday in the second-to-last column on a Sunday and you land on
 * Sunday, one row up. In a grid that is a teleport, not a move, and in a
 * keyboard-first product it is how people lose their place. Clamping by COLUMN
 * means the cursor stops at the edge and stays on the weekday it was on.
 *
 * `columns` may be Infinity, which means "as far as this weekday goes".
 * columnStep : Number Number Number -> Number
 */
export function columnStep(cursor: number, columns: number, todayIndex: number): number {
  const row = cursor % ROWS
  const column = Math.floor(cursor / ROWS)
  const target = Math.max(0, Math.min(column + columns, lastColumnOf(row, todayIndex)))
  return Math.max(0, Math.min(target * ROWS + row, todayIndex))
}

/**
 * Where to hang the tooltip so it never leaves the grid.
 *
 * Centring on the square is right in the middle of a year and wrong at both
 * ends, where a centred tooltip would hang off the edge and be clipped by the
 * scroll container. Near an edge it anchors to that edge instead and opens
 * inwards. Flipping rather than clamping means the tooltip never drifts away
 * from the square it is describing by more than its own width, and needs no
 * measurement of the rendered text.
 * tipAnchorOf : Number Number -> {left, transform}
 */
function tipAnchorOf(centre: number, innerWidth: number): { left: number; transform: string } {
  if (centre - TIP_HALF < 0) return { left: centre - CELL / 2, transform: 'none' }
  if (centre + TIP_HALF > innerWidth) return { left: centre + CELL / 2, transform: 'translateX(-100%)' }
  return { left: centre, transform: 'translateX(-50%)' }
}

/**
 * Which columns get a month label.
 *
 * A label goes on the first column of each new month, but never within three
 * columns of the previous one — "Apr May" crushed together is worse than no
 * label at all. The first column is skipped because it is usually mid-month.
 * monthLabelsFor : [[ContributionDay]] -> [{w, text}]
 */
function monthLabelsFor(
  weeks: readonly (readonly ContributionDay[])[],
): { w: number; text: string }[] {
  const out: { w: number; text: string }[] = []
  let lastLabelled = -3
  for (let w = 1; w < weeks.length; w++) {
    const month = weeks[w][0].date.slice(0, 7)
    const previous = weeks[w - 1][0].date.slice(0, 7)
    if (month !== previous && w - lastLabelled >= 3) {
      out.push({ w, text: MONTH_FMT.format(asDate(weeks[w][0].date)) })
      lastLabelled = w
    }
  }
  return out
}
